// Renders the current countdown as a shareable image card, so the whole
// point of the app (this is funny, send it to the group chat) doesn't
// require a screenshot. Canvas-drawn rather than an HTML-to-image library,
// since the card only needs a handful of text blocks and a background.

export interface ShareCardData {
  eventName: string;
  eventDate: string;
  daysLeft: number;
  punchlineMain: string;
  punchlineSimile: string;
}

const CARD_W = 1080;
const CARD_H = 1350;

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

async function ensureFontLoaded(): Promise<void> {
  try {
    await document.fonts.load("900 64px 'Schibsted Grotesk'");
    await document.fonts.ready;
  } catch {
    // Falls back to the canvas default sans-serif — still legible.
  }
}

export async function renderShareCard(data: ShareCardData): Promise<HTMLCanvasElement> {
  await ensureFontLoaded();

  const canvas = document.createElement("canvas");
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext("2d")!;
  const pad = 84;
  const accent = "#ff5c1f";
  const ink = "#f2f0ea";
  const inkDim = "rgba(242, 240, 234, .6)";

  // Background: near-black with a soft orange glow, echoing the app's night sky.
  ctx.fillStyle = "#04050a";
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  const glow = ctx.createRadialGradient(CARD_W * 0.8, CARD_H * 0.15, 0, CARD_W * 0.8, CARD_H * 0.15, CARD_W * 0.9);
  glow.addColorStop(0, "rgba(255, 92, 31, .28)");
  glow.addColorStop(1, "rgba(255, 92, 31, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // A handful of static stars, since we can't animate a still image.
  let seed = 42;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return (seed % 1000) / 1000;
  };
  ctx.fillStyle = "rgba(242, 240, 234, .5)";
  for (let i = 0; i < 70; i++) {
    const x = rand() * CARD_W;
    const y = rand() * CARD_H;
    const r = rand() * 1.6 + 0.4;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  let y = pad + 40;

  // Wordmark
  ctx.fillStyle = accent;
  const badgeSize = 56;
  const badgeR = 16;
  ctx.beginPath();
  ctx.moveTo(pad + badgeR, y);
  ctx.arcTo(pad + badgeSize, y, pad + badgeSize, y + badgeSize, badgeR);
  ctx.arcTo(pad + badgeSize, y + badgeSize, pad, y + badgeSize, badgeR);
  ctx.arcTo(pad, y + badgeSize, pad, y, badgeR);
  ctx.arcTo(pad, y, pad + badgeSize, y, badgeR);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#0c0c0c";
  ctx.font = "900 34px 'Schibsted Grotesk', sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText("*", pad + badgeSize / 2, y + badgeSize / 2 + 2);

  ctx.fillStyle = ink;
  ctx.font = "800 34px 'Schibsted Grotesk', sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("C*UNTDOWN", pad + badgeSize + 18, y + badgeSize / 2 + 1);

  y += badgeSize + 64;

  // Event name + date
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = accent;
  ctx.font = "700 30px 'Schibsted Grotesk', sans-serif";
  ctx.fillText(data.eventName.toUpperCase(), pad, y);
  ctx.fillStyle = inkDim;
  ctx.font = "600 26px 'Schibsted Grotesk', sans-serif";
  ctx.textAlign = "right";
  ctx.fillText(data.eventDate, CARD_W - pad, y);
  ctx.textAlign = "left";

  y += 90;

  // Big days-left number
  ctx.fillStyle = ink;
  ctx.font = "900 220px 'Schibsted Grotesk', sans-serif";
  ctx.fillText(String(data.daysLeft).replace(/\B(?=(\d{3})+(?!\d))/g, ","), pad, y + 170);
  y += 230;

  ctx.fillStyle = accent;
  ctx.font = "800 44px 'Schibsted Grotesk', sans-serif";
  ctx.fillText(data.daysLeft === 1 ? "DAY LEFT" : "DAYS LEFT", pad, y);

  y += 100;

  // Divider
  ctx.strokeStyle = "rgba(242, 240, 234, .16)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(pad, y);
  ctx.lineTo(CARD_W - pad, y);
  ctx.stroke();

  y += 80;

  // Punchline
  const maxWidth = CARD_W - pad * 2;
  ctx.fillStyle = accent;
  ctx.font = "800 46px 'Schibsted Grotesk', sans-serif";
  const mainLines = wrapLines(ctx, data.punchlineMain, maxWidth);
  for (const line of mainLines) {
    ctx.fillText(line, pad, y);
    y += 58;
  }

  if (data.punchlineSimile) {
    y += 24;
    ctx.font = "700 36px 'Schibsted Grotesk', sans-serif";
    const simileLines = wrapLines(ctx, data.punchlineSimile, maxWidth);
    for (const line of simileLines) {
      ctx.fillText(line, pad, y);
      y += 46;
    }
  }

  // Footer
  ctx.fillStyle = "rgba(242, 240, 234, .4)";
  ctx.font = "600 24px 'Schibsted Grotesk', sans-serif";
  ctx.fillText("C*UNTDOWN — install it, don't just screenshot it.", pad, CARD_H - pad + 8);

  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export type ShareResult = "shared" | "downloaded" | "failed";

/**
 * Tries the native share sheet first (so it can go straight to a group
 * chat), falling back to a plain download when the browser can't share
 * files — desktop Safari/Chrome, mostly.
 */
export async function shareCountdownCard(data: ShareCardData): Promise<ShareResult> {
  try {
    const canvas = await renderShareCard(data);
    const blob = await canvasToBlob(canvas);
    if (!blob) return "failed";

    const file = new File([blob], "cuntdown.png", { type: "image/png" });
    const shareData = {
      files: [file],
      title: "C*UNTDOWN",
      text: `${data.daysLeft} days left. ${data.punchlineMain}`,
    };

    const nav = navigator as Navigator & { canShare?: (data?: ShareData) => boolean };
    if (nav.canShare?.(shareData) && navigator.share) {
      await navigator.share(shareData);
      return "shared";
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cuntdown.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return "downloaded";
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      // User cancelled the share sheet — not a failure.
      return "shared";
    }
    return "failed";
  }
}
